/**
 * @module Actions/collectors
 * @desc All collectors actions
 */
// Types
import type { Action } from "src/types/common";
import type { TableCollector, GroupWithDedicatedDataTypes } from "src/types/collectors";
// Constants
import * as types from "./collectors.constants";

class CollectorsActionsModel {
	/**
	 * get list of table collectors
	 * @return {Action}
	 */
	public getTableCollectors(): Action {
		return {
			type: types.SAGAS_GET_TABLE_COLLECTORS,
		};
	}

	/**
	 * get extracted table collector
	 * @return {Action}
	 */
	public getExtractedTableCollector(data: any): Action {
		return {
			type: types.SAGAS_GET_EXTRACTED_TABLE_COLLECTOR,
			payload: data,
		};
	}

	/**
	 * get list of table collectors
	 * @return {Action}
	 */
	public setTableCollectors(data: any): Action {
		return {
			type: types.SET_TABLE_COLLECTORS,
			payload: data,
		};
	}

	/**
	 * set selected table collector
	 * @return {Action}
	 */
	public setSelectedTableCollector(data: any): Action {
		return {
			type: types.SET_SELECTED_TABLE_COLLECTOR,
			payload: data,
		};
	}

	/**
	 * set list of extracted table collector
	 * @return {Action}
	 */
	public setExtractedTableCollector(data: any): Action {
		return {
			type: types.SET_EXTRACTED_TABLE_COLLECTOR,
			payload: data,
		};
	}

	/**
	 * set list of collector documents
	 * @return {Action<TableCollector[]>}
	 */
	public setCollectorDocuments(data: TableCollector[]): Action<TableCollector[]> {
		return {
			type: types.SET_COLLECTOR_DOCUMENTS,
			payload: data,
		};
	}

	/**
	 * set loading (loosened signature to accept custom loading states dynamically)
	 * @param {any} data
	 * @return {any}
	 */
	public setLoading(data: any): any {
		return {
			type: types.SET_LOADING,
			payload: data,
		};
	}

	/**
	 * set ts numbers with dedicated version list
	 * @param {GroupWithDedicatedDataTypes[]} data
	 * @return {Action<GroupWithDedicatedDataTypes[]>}
	 */
	public setDataVersionsList(data: GroupWithDedicatedDataTypes[]): Action<GroupWithDedicatedDataTypes[]> {
		return {
			type: types.SET_DATAVERSIONS_LIST,
			payload: data,
		};
	}

	/**
	 * set selected ts number
	 * @param {string} data
	 * @return {Action<string>}
	 */
	public setSelectedGroup(data: string): Action<string> {
		return {
			type: types.SET_SELECTED_GROUP,
			payload: data,
		};
	}

	/**
	 * set selected ts number
	 * @param {string} data
	 * @return {Action<string>}
	 */
	public setSelectedDataType(data: string): Action<string> {
		return {
			type: types.SET_SELECTED_DATA_TYPE,
			payload: data,
		};
	}

	/**
	 * set selected version
	 * @param {string} data
	 * @return {Action<string>}
	 */
	public setSelectedVersion(data: string): Action<string> {
		return {
			type: types.SET_SELECTED_VERSION,
			payload: data,
		};
	}

	/**
	 * set version list
	 * @param {string[]} data
	 * @return {Action<string[]>}
	 */
	public setVersionList(data: string[]): Action<string[]> {
		return {
			type: types.SET_VERSION_LIST,
			payload: data,
		};
	}

	/**
	 * set data version modal open state
	 * @param {boolean} data
	 * @return {Action<boolean>}
	 */
	public setDataVersionModalOpened(data: boolean): Action<boolean> {
		return {
			type: types.SET_DATAVERSION_MODAL_OPEN,
			payload: data,
		};
	}

	/**
	 * set selected document
	 * @param {Partial<TableCollector>} data
	 * @return {Action<Partial<TableCollector>>}
	 */
	public setSelectedDocument(data: Partial<TableCollector>): Action<Partial<TableCollector>> {
		return {
			type: types.SET_SELECTED_DOCUMENT,
			payload: data,
		};
	}

	/**
	 * handle toc open
	 * @return {boolean} open
	 * @return {Action<boolean>}
	 */
	public handleTocOpen(open: boolean): Action<boolean> {
		return {
			type: types.HANDLE_TOC_OPEN,
			payload: open,
		};
	}

	public getEntities(callback?: Function): any {
		return {
			type: types.SAGAS_GET_ENTITIES,
			callback,
		};
	}

	public setEntities(data: any[]): any {
		return {
			type: types.SET_ENTITIES,
			payload: data,
		};
	}

	public getDBColumns(entity: string, callback?: Function): any {
		return {
			type: types.SAGAS_GET_DB_COLUMNS,
			payload: entity,
			callback,
		};
	}

	public setDBColumns(data: any[]): any {
		return {
			type: types.SET_DB_COLUMNS,
			payload: data,
		};
	}

	public getPreprocessingFunctions(callback?: Function): any {
		return {
			type: types.SAGAS_GET_PREPROCESSING_FUNCTIONS,
			callback,
		};
	}

	public setPreprocessingFunctions(data: any[]): any {
		return {
			type: types.SET_PREPROCESSING_FUNCTIONS,
			payload: data,
		};
	}

	public getAutoExtractorConfigs(docId: string, callback?: Function): any {
		return {
			type: types.SAGAS_GET_AUTO_EXTRACTOR_CONFIGS,
			payload: docId,
			callback,
		};
	}

	public setDocumentConfigs(data: any[]): any {
		return {
			type: types.SET_DOCUMENT_CONFIGS,
			payload: data,
		};
	}

	public saveAutoExtractorConfig(
		data: { id?: string; payload: any },
		callback?: Function,
		errorCallback?: Function,
	): any {
		return {
			type: types.SAGAS_SAVE_AUTO_EXTRACTOR_CONFIG,
			payload: data,
			callback,
			errorCallback,
		};
	}

	public postAutoExtractorPreview(
		data: { id: string; type: string },
		callback?: Function,
		errorCallback?: Function,
	): any {
		return {
			type: types.SAGAS_POST_AUTO_EXTRACTOR_PREVIEW,
			payload: data,
			callback,
			errorCallback,
		};
	}

	public getAutoExtractorPreviewJobDetails(jobId: string, callback?: Function, errorCallback?: Function): any {
		return {
			type: types.SAGAS_GET_AUTO_EXTRACTOR_PREVIEW_JOB,
			payload: jobId,
			callback,
			errorCallback,
		};
	}

	/**
	 * export selected table collector (excel | json)
	 */
	public exportTable(format: "excel" | "json" = "excel"): Action<"excel" | "json"> {
		return {
			type: types.SAGAS_EXPORT_TABLE,
			payload: format,
		};
	}
}

const CollectorsActions = new CollectorsActionsModel();

export default CollectorsActions;
